import { inject, Injectable } from '@angular/core';
import { CartItem, CouponConfigurationResponse, EndOrderDetails } from '@pedix-workspace/utils';
import { ShippingCostResponse } from '@pedix-workspace/shared-delivery';
import { calculateCartItemsTotal } from '@pedix-workspace/orders';
import { CouponValidationService } from '@pedix-workspace/pedixapp-core-services';
import { EndOrderForm } from './end-order-form/end-order-form.service';

export type EndOrderTotals = Pick<
  EndOrderDetails,
  | 'totalAmount'
  | 'totalAmountWithDiscount'
  | 'totalProductDiscount'
  | 'couponDiscount'
  | 'deliveryCost'
  | 'outOfDeliveryZone'
  | 'paymentMethodExtraCharge'
  | 'paymentMethodDiscount'
  | 'foodBankDonationAmount'
  | 'finalAmount'
>;

export type EndOrderTotalsParams = {
  cartItems: CartItem[];
  couponConfigurationSnapshot?: CouponConfigurationResponse;
  shippingCostResponse?: Pick<ShippingCostResponse, 'deliveryCost' | 'outOfDeliveryZone'>;
  endOrderForm?: EndOrderForm;
  foodBankDonation?: number;
};

@Injectable({ providedIn: 'root' })
export class EndOrderTotalsService {
  private couponValidationService = inject(CouponValidationService);

  getEndOrderTotals({
    cartItems,
    endOrderForm,
    couponConfigurationSnapshot,
    shippingCostResponse,
    foodBankDonation,
  }: EndOrderTotalsParams): EndOrderTotals {
    const totalAmount = calculateCartItemsTotal(cartItems, {
      usePriceDiscount: false,
    });
    const totalAmountWithDiscount = calculateCartItemsTotal(cartItems, {
      usePriceDiscount: true,
    });
    const totalProductDiscount = totalAmount - totalAmountWithDiscount;

    const couponDiscount = couponConfigurationSnapshot
      ? this.couponValidationService.calculateCouponDiscount(cartItems, couponConfigurationSnapshot)
      : 0;

    const paymentMethodDiscount = this.calculatePaymentMethodDiscountAmount({
      discount: endOrderForm?.paymentMethod?.discount,
      totalAmountWithDiscount,
      couponDiscount,
    });

    const deliveryCost = shippingCostResponse?.deliveryCost || 0;

    const paymentMethodExtraCharge = this.calculatePaymentMethodExtraChargeAmount({
      extraCharge: endOrderForm?.paymentMethod?.extraCharge,
      totalAmountWithDiscount,
      couponDiscount,
      deliveryCost,
    });
    const foodBankDonationAmount = endOrderForm?.bankFoodDonationChecked ? foodBankDonation : 0;

    const finalAmount = this.calculateTotalToBePaid({
      totalAmount,
      totalProductDiscount,
      couponDiscount,
      paymentMethodDiscount,
      deliveryCost,
      paymentMethodExtraCharge,
      foodBankDonationAmount,
    });

    return {
      totalAmount,
      totalAmountWithDiscount,
      totalProductDiscount,
      deliveryCost,
      couponDiscount,
      paymentMethodExtraCharge,
      paymentMethodDiscount,
      finalAmount,
      outOfDeliveryZone: shippingCostResponse?.outOfDeliveryZone || false,
      // Food Bank Donation
      foodBankDonationAmount,
    };
  }

  calculatePaymentMethodDiscountAmount(data: {
    totalAmountWithDiscount: number;
    couponDiscount?: number;
    discount?: number;
  }) {
    if (!data.discount) {
      return 0;
    }
    const subtotal = data.totalAmountWithDiscount - (data.couponDiscount || 0);

    return (subtotal * data.discount) / 100;
  }

  calculatePaymentMethodExtraChargeAmount(data: {
    totalAmountWithDiscount: number;
    couponDiscount?: number;
    deliveryCost?: number;
    extraCharge?: number;
  }) {
    if (!data.extraCharge) {
      return 0;
    }
    const subtotal =
      data.totalAmountWithDiscount - (data.couponDiscount || 0) + (data.deliveryCost || 0);

    return (subtotal * data.extraCharge) / 100;
  }

  calculateTotalToBePaid(
    data: Pick<
      EndOrderDetails,
      | 'totalAmount'
      | 'totalProductDiscount'
      | 'couponDiscount'
      | 'paymentMethodDiscount'
      | 'deliveryCost'
      | 'paymentMethodExtraCharge'
      | 'foodBankDonationAmount'
    >,
  ) {
    const subtotal =
      data.totalAmount -
      data.totalProductDiscount -
      (data.couponDiscount || 0) -
      (data.paymentMethodDiscount || 0) +
      (data.deliveryCost || 0) +
      (data.paymentMethodExtraCharge || 0);

    return subtotal + (data.foodBankDonationAmount || 0);
  }
}
